import { useCallback } from 'react'
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  restrictToParentElement,
  restrictToVerticalAxis,
} from '@dnd-kit/modifiers'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useReactFlow } from '@xyflow/react'
import { produce } from 'immer'
import { nanoid } from 'nanoid'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import type { BuilderNodeType } from '@/features/flow/_builder/components/blocks/types'
import type { MenuNodeData } from '../../../../nodes/menu-node/menu.node'
import { MenuOptionProperty } from './components/menu-option-property'

type MenuNodePropertyPanelProps = Readonly<{
  id: string
  type: BuilderNodeType
  data: MenuNodeData
  updateData: (data: Partial<MenuNodeData>) => void
}>

export default function MenuNodePropertyPanel({
  id,
  data,
  updateData,
}: MenuNodePropertyPanelProps) {
  const { setNodes, setEdges } = useReactFlow()

  const MAX_OPTIONS = 10

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const handleRemoveNodeOption = useCallback(
    (optionId: string) => {
      setNodes((nodes) =>
        produce(nodes, (draft) => {
          const node = draft.find((n) => n.id === id)

          if (node) {
            const options = node.data.options as MenuNodeData['options']
            const pathIndex = options.findIndex((p) => p.id === optionId)

            if (pathIndex !== -1) {
              options.splice(pathIndex, 1)

              options
                .sort((a, b) => a.option.id - b.option.id)
                .forEach((option, index) => {
                  option.option.id = index
                })
            }
          }
        })
      )

      setEdges((edges) =>
        edges.filter((edge) => edge.sourceHandle !== optionId)
      )
    },
    [id, setEdges, setNodes]
  )

  const handleUpdateOption = useCallback(
    (index: number, option: MenuNodeData['options'][number]) => {
      updateData({
        options: data.options.map((o, i) => (i === index ? option : o)),
      })
    },
    [data.options, updateData]
  )

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event

    if (over && active.id !== over.id) {
      updateData({
        options: data.options.map((option) => {
          if (option.id === active.id) {
            return {
              ...option,
              option: {
                ...option.option,
                // biome-ignore lint/style/noNonNullAssertion:
                id: data.options.find((o) => o.id === over.id)!.option.id,
              },
            }
          }
          if (option.id === over.id) {
            return {
              ...option,
              option: {
                ...option.option,
                id: data.options.find((o) => o.id === active.id)!.option.id,
              },
            }
          }
          return option
        }),
      })
    }
  }

  const handleAddNodePath = useCallback(() => {
    if (data.options.length >= MAX_OPTIONS) return

    setNodes((nodes) =>
      produce(nodes, (draft) => {
        const node = draft.find((n) => n.id === id)

        if (node) {
          ;(node.data.options as MenuNodeData['options']).push({
            id: nanoid(),
            option: {
              id: data.options.length,
              value: `Option ${data.options.length + 1}`,
            },
          })
        }
      })
    )
  }, [id, setNodes, data.options])

  return (
    <div className='flex flex-col gap-4 p-4'>
      <div className='-mb-4 self-end text-xs text-card-foreground/20'>{id}</div>

      <div className='flex flex-col'>
        <div className='text-xs font-semibold text-card-foreground/60'>
          Pergunta
        </div>

        <div className='mt-2 flex'>
          <Input
            type='text'
            value={data.question || ''}
            onChange={(e) => updateData({ question: e.target.value })}
            placeholder='Digite sua pergunta aqui...'
          />
        </div>
        <div className='mt-4 text-xs font-semibold text-card-foreground/60'>
          Opções
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
          modifiers={[restrictToVerticalAxis, restrictToParentElement]}
        >
          <SortableContext
            items={data.options}
            strategy={verticalListSortingStrategy}
          >
            {Array.from(data.options)
              .sort((a, b) => a.option.id - b.option.id)
              .map((option) => (
                <MenuOptionProperty
                  key={option.id}
                  option={option}
                  handleRemoveNodeOption={handleRemoveNodeOption}
                  handleUpdateOption={handleUpdateOption}
                />
              ))}
          </SortableContext>
        </DndContext>

        <Button
          className='mt-4'
          size={'sm'}
          variant={'outline'}
          onClick={handleAddNodePath}
          disabled={data.options.length >= MAX_OPTIONS}
        >
          Adicionar opção
        </Button>
        <div className='mt-1 text-right text-[10px] text-card-foreground/50'>
          {data.options.length}/{MAX_OPTIONS}
        </div>
      </div>
    </div>
  )
}
